import { TransactionResponse } from 'ethers';
import { Bundles__factory, Multicall__factory } from '~/public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import type { ContractCallParams } from '~/types/Web3Provider';
import { useWalletInfo, useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import type { BundlesData } from '~/types/bundles';
import { apiUrls, Tokens } from '~/utils/constants';
import type { IListItem } from '~/types/list';
import { bundles as bundlesImages } from 'public/img/bundles';
import { type MultiTokenPackItem } from '~/types/marketplace';
import { formatPrice } from '~/utils/formatters';

enum BundleAssets {
  Metallurgist = 'metallurgist',
  Builder = 'builder',
  Carpenter = 'carpenter',
  Beginner = 'beginner'
}

interface IUseBuyBundle {
  bundles: Ref<MultiTokenPackItem[] | undefined>;
  activeBundle: Ref<MultiTokenPackItem | undefined>;
  purchasedBundle: Ref<MultiTokenPackItem | null>;
  showBundleModal: Ref<boolean>;
  isButtonLoading: Ref<boolean>;
  showInsufficientFundsDialog: Ref<boolean>;
  showErrorDialog: Ref<boolean>;
  showSuccessDialog: Ref<boolean>;
  linkToTransaction: Ref<string>;
  transactionPrice: Ref<string>;
  errorMessage: Ref<string>;
  handleClickOpen: (bundleId: number) => Promise<void>;
  handleClickBuy: () => Promise<void>;
  resetActiveBundle: () => void;
  resetPurchasedBundle: () => void;
  isEnoughBalance: Ref<boolean>;
}

interface IMulticallResultItem {
  bundleId: number;
  remaining: number;
  timeToReset: number;
}

export default async (): Promise<IUseBuyBundle> => {
  const { getProvider } = useProvider();
  const { t } = useI18n();
  const { address } = useWeb3ModalAccount();
  const { walletInfo } = useWalletInfo();
  const { sendContractMethod } = useSendContractMethod();
  const { apiUrl, blockchain } = useEnvs();
  const store = useMainStore();
  const { login } = useAuth();

  const { allowanceBundles } = useTokensReader();
  const { approveSpendUsdt } = useStakingMethods();
  const { getSigner } = useProvider();
  const usdtContract = ref(blockchain.contracts.usdt);
  const { balance } = await useBalance(usdtContract);

  const purchasedBundleId = ref<number | null>(null);
  const activeBundleId = ref<number | null>(null);
  const bundlesAvailabilityData = ref<IMulticallResultItem[]>([]);
  const isButtonLoading = ref(false);
  const showBundleModal = ref(false);
  const showInsufficientFundsDialog = ref(false);
  const showErrorDialog = ref(false);
  const showSuccessDialog = ref(false);
  const errorMessage = ref('');
  const linkToTransaction = ref('');
  const transactionPrice = ref('');
  const bundlesData = ref<BundlesData | null>(null);
  const purchasedBundle = ref<MultiTokenPackItem | null>(null);

  const loadBundlesData = async () => {
    try {
      bundlesData.value = await $fetch<BundlesData>(apiUrls.bundles, {
        baseURL: apiUrl
      });
    } catch (error) {
      console.error('Error loading bundles data:', error);
      bundlesData.value = null;
    }
  };

  watch(
    address,
    async () => {
      activeBundleId.value = null;
      showBundleModal.value = false;

      if (bundlesAvailabilityData.value) {
        bundlesAvailabilityData.value = [];
      }
      await loadBundlesData();
    },
    { immediate: true }
  );

  const handleClickOpen = async (bundleId: number): Promise<void> => {
    if (!address.value) {
      await login();
      return;
    }

    activeBundleId.value = bundleId;
    showBundleModal.value = true;
  };

  const handleClickBuy = async (): Promise<void> => {
    if (!activeBundle.value) {
      return;
    }

    if (!activeBundle.value.isAvailable) {
      console.warn('Bundle is not available for purchase');
      return;
    }

    try {
      const { bundleId, title, priceAmount } = activeBundle.value;
      await buyBundle(bundleId, priceAmount, title);
    } catch (error) {
      console.error(error);
    } finally {
      activeBundleId.value = null;
      showBundleModal.value = false;
      await loadBundlesData();
    }
  };

  const resetActiveBundle = () => {
    activeBundleId.value = null;
  };

  const resetPurchasedBundle = () => {
    purchasedBundleId.value = null;
    purchasedBundle.value = null;
  };

  const getTokenKey = (tokenAddress: string) => {
    return tokenMetaData(blockchain.contracts, tokenAddress)?.name.contract as Tokens;
  };

  const getTokenName = (tokenAddress: string) => {
    return tokenMetaData(blockchain.contracts, tokenAddress)?.methodName;
  };

  watch([bundlesData, address], async () => {
    if (!bundlesData.value?.bundles) {
      bundlesAvailabilityData.value = [];
      return;
    }

    try {
      const multicallContract = Multicall__factory.connect(blockchain.multicallAddress, getProvider());
      const bundlesInterface = Bundles__factory.createInterface();

      const bundleIdMap = new Map<string, number>();
      const callData = bundlesData.value.bundles.map((bundle) => {
        const encodedCall = bundlesInterface.encodeFunctionData('getRemainingInInterval(uint256,address)', [
          bundle.bundleId,
          address.value ? address.value : '0x0000000000000000000000000000000000000001'
        ]);
        bundleIdMap.set(encodedCall, bundle.bundleId);

        return [blockchain.contracts.bundles, encodedCall];
      });

      const multicallResult = await multicallContract?.tryAggregate.staticCall(
        true,
        callData.map((item) => ({
          target: item[0],
          callData: item[1]
        }))
      );

      bundlesAvailabilityData.value = multicallResult.map((result: [boolean, string], index: number) => {
        const originalCallDataKey = callData[index][1];
        const bundleId = bundleIdMap.get(originalCallDataKey);

        if (!result[0]) {
          return {
            bundleId: bundleId || 0,
            remaining: 0,
            timeToReset: 0
          };
        }

        const decoded = bundlesInterface.decodeFunctionResult('getRemainingInInterval(uint256,address)', result[1]);
        const remaining = BigNumber(decoded[0]).toNumber();
        const timeToReset = BigNumber(decoded[1]).toNumber();

        return {
          bundleId,
          remaining,
          timeToReset
        } as IMulticallResultItem;
      });
    } catch (error) {
      console.error('Error fetching bundles availability:', error);
      bundlesAvailabilityData.value = [];
    }
  });

  const getBundleAsset = (bundleName: string) => {
    if (!bundleName) {
      return bundlesImages.bundleFallback;
    }

    const bundleId = Object.values(BundleAssets).find((key) => bundleName.toLowerCase().includes(key));
    if (!bundleId) {
      return bundlesImages.bundleFallback;
    }

    return bundlesImages[bundleId as keyof typeof bundlesImages];
  };

  const bundles = computed(() => {
    if (!bundlesData.value?.bundles || bundlesAvailabilityData.value.length === 0) {
      return [];
    }

    const allBundles = bundlesData.value.bundles
      .map((bundle) => {
        const { bundleId, priceAmount, originalPriceAmount, items, nameKey } = bundle;
        const availability = bundlesAvailabilityData.value.find(
          (item: IMulticallResultItem) => item.bundleId === bundleId
        );

        if (!availability) {
          return null;
        }

        const listItems: IListItem[] = items
          .map((item) => {
            const primaryTokenQuantity = Number(item.primaryTokenQuantity);
            const secondaryTokenQuantity = Number(item.secondaryTokenQuantity);

            const primaryToken = {
              key: getTokenKey(item.stakingContract),
              name: getTokenName(item.stakingContract),
              amount: addThousandSeparators(primaryTokenQuantity)
            };

            const secondaryTokenAddress = blockchain.contracts[primaryToken.key]?.addresses.secondaryToken;

            if (secondaryTokenQuantity > 0 && secondaryTokenAddress) {
              const secondaryToken = {
                key: getTokenKey(secondaryTokenAddress),
                name: getTokenName(secondaryTokenAddress),
                amount: addThousandSeparators(secondaryTokenQuantity)
              };

              return [primaryToken, secondaryToken];
            }

            return primaryToken;
          })
          .flat();

        return {
          bundleId,
          /*

          Example implementation of the new formatter:

          priceAmount: Formatters.number.renderNumberPartsToString(Formatters.number.formatUSDNumber(priceAmount)),
          originalPriceAmount: Formatters.number.renderNumberPartsToString(
            Formatters.number.formatUSDNumber(originalPriceAmount)
          ),

           */
          priceAmount: formatPrice(priceAmount),
          originalPriceAmount: formatPrice(originalPriceAmount),
          title: nameKey || 'bundleFallback',
          src: getBundleAsset(nameKey),
          items: listItems,
          isAvailable: availability.remaining > 0,
          isOneTimeOffer: availability.remaining === 1,
          remainingCount: availability.remaining,
          timeToReset: availability.timeToReset
        } as MultiTokenPackItem;
      })
      .filter((bundle): bundle is MultiTokenPackItem => bundle !== null && bundle.isAvailable);

    const sortBundles = (bundles: MultiTokenPackItem[]) => {
      return bundles.sort((a, b) => {
        const priceA = Number(a.priceAmount.replace(/,/g, ''));
        const priceB = Number(b.priceAmount.replace(/,/g, ''));

        if (priceA === priceB) {
          return b.items.length - a.items.length;
        }

        return priceA - priceB;
      });
    };

    const basicBundles = allBundles.filter((bundle) => !bundle.isOneTimeOffer);
    const oneTimeBundles = sortBundles(allBundles.filter((bundle) => bundle.isOneTimeOffer));
    const metallurgistBundles = sortBundles(
      basicBundles.filter((bundle) => bundle.title.toLowerCase().includes(BundleAssets.Metallurgist))
    );
    const builderBundles = sortBundles(
      basicBundles.filter((bundle) => bundle.title.toLowerCase().includes(BundleAssets.Builder))
    );
    const carpenterBundles = sortBundles(
      basicBundles.filter((bundle) => bundle.title.toLowerCase().includes(BundleAssets.Carpenter))
    );
    const beginnerBundles = sortBundles(
      basicBundles.filter((bundle) => bundle.title.toLowerCase().includes(BundleAssets.Beginner))
    );

    return [...oneTimeBundles, ...metallurgistBundles, ...builderBundles, ...carpenterBundles, ...beginnerBundles];
  });

  const activeBundle = computed(() => {
    if (!activeBundleId.value || !bundles.value) {
      return;
    }

    return bundles.value.find((bundle) => bundle.bundleId === activeBundleId.value);
  });

  const setPurchasedBundle = () => {
    if (!purchasedBundleId.value || !bundles.value) {
      return;
    }

    const bundleItem = bundles.value.find((bundle) => bundle.bundleId === purchasedBundleId.value);

    if (!bundleItem) {
      return;
    }

    purchasedBundle.value = bundleItem;
  };

  const isEnoughBalance = computed(() => {
    if (!balance.value || !activeBundle.value) {
      return false;
    }

    return new BigNumber(balance.value).gte(BigNumber(activeBundle.value.priceAmount));
  });

  const sendContractMethodExtended = async <T extends Record<any, any>, M extends keyof T>(
    sendParams: ContractCallParams<T, M>,
    onConfirmation?: (confirmations?: number) => void
  ) => {
    const from = address.value;
    return sendContractMethod({ ...sendParams, from }, onConfirmation);
  };

  const buyBundleContract = async (bundleId: number) => {
    return sendContractMethodExtended({
      contract: Bundles__factory.connect(blockchain.contracts.bundles, await getSigner()),
      methodName: 'purchaseBundle',
      args: [bundleId]
    });
  };

  const buyBundle = async (bundleId: number, price: string, bundleName: string) => {
    try {
      if (!balance.value || !address.value) {
        return;
      }

      isButtonLoading.value = true;

      const isEnoughBalance = new BigNumber(balance.value).gte(BigNumber(price));

      if (!isEnoughBalance) {
        transactionPrice.value = price;
        showInsufficientFundsDialog.value = true;
        return;
      }

      const allowedCount = await allowanceBundles(address.value);

      const shouldCallApprove = new BigNumber(allowedCount).isLessThan(price);

      if (shouldCallApprove) {
        const transactionApprove = await approveSpendUsdt(BigNumber(price).toNumber(), blockchain.contracts.bundles);

        if (!transactionApprove?.hash) {
          return;
        }

        saveHashToLocalStorage(t('notificationApprove'), transactionApprove.hash);
        store.updateVersion();

        await transactionApprove?.wait();
      }

      const transactionBuy: TransactionResponse | undefined = await buyBundleContract(bundleId);

      if (!transactionBuy?.hash) {
        return;
      }

      saveHashToLocalStorage(`${t('notificationBuy')}: ${t(bundleName)}`, transactionBuy.hash);
      store.updateVersion();

      await transactionBuy.wait();

      modifyHistoryHashStatus(transactionBuy?.hash, 'Done');

      store.updateVersion();
      store.updateBuypackCounter();

      purchasedBundleId.value = bundleId;
      transactionPrice.value = price;
      linkToTransaction.value = blockchain.blockScanUrl + '/tx/' + transactionBuy?.hash;
      setPurchasedBundle();

      showSuccessDialog.value = true;
    } catch (error: unknown) {
      const currentProviderKind = walletInfo.value?.name;

      if (currentProviderKind) {
        errorMessage.value = getCancelErrorMessage(error, currentProviderKind);
      }

      showErrorDialog.value = true;

      console.error(error);
    } finally {
      isButtonLoading.value = false;
    }
  };

  return {
    bundles,
    isEnoughBalance,
    showBundleModal,
    showInsufficientFundsDialog,
    showErrorDialog,
    showSuccessDialog,
    isButtonLoading,
    transactionPrice,
    linkToTransaction,
    errorMessage,
    handleClickOpen,
    handleClickBuy,
    activeBundle,
    purchasedBundle,
    resetActiveBundle,
    resetPurchasedBundle
  };
};
